import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Code2, Shield, Gamepad2, Cloud, Palette, ArrowUpRight } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';

const services = [
  {
    num: '01',
    icon: Brain,
    title: 'AI Development',
    desc: 'Production-ready AI — from LLM apps to deployed models with evals, guardrails, and monitoring baked in.',
    services: ['LLM Apps & Agents (RAG)', 'Fine-tuning & Optimization', 'Model Evals & Guardrails', 'Vision, NLP & Speech'],
    tools: ['TensorFlow', 'PyTorch', 'OpenAI', 'AWS'],
    accent: '#00D4FF',
    span: 'md:col-span-4 md:row-span-2',
    size: 'lg',
  },
  {
    num: '02',
    icon: Code2,
    title: 'Full-Stack',
    desc: 'High-performance web & mobile apps with cloud-native architecture.',
    tools: ['React', 'Node', 'Python', 'AWS'],
    accent: '#0099FF',
    span: 'md:col-span-2',
    size: 'sm',
  },
  {
    num: '03',
    icon: Shield,
    title: 'Blockchain & Web3',
    desc: 'Smart contracts, DeFi protocols, and NFT marketplaces.',
    tools: ['Solidity', 'Ethereum', 'Polygon'],
    accent: '#00FF88',
    span: 'md:col-span-2',
    size: 'sm',
  },
  {
    num: '04',
    icon: Gamepad2,
    title: 'Game Development',
    desc: 'Immersive experiences with polished mechanics and multiplayer networking.',
    tools: ['Unity', 'Unreal', 'WebGL'],
    accent: '#C850C0',
    span: 'md:col-span-3',
    size: 'md',
  },
  {
    num: '05',
    icon: Cloud,
    title: 'Cloud & DevOps',
    desc: 'Scalable infrastructure, CI/CD pipelines, and monitoring.',
    tools: ['AWS', 'Docker', 'K8s', 'Terraform'],
    accent: '#7B61FF',
    span: 'md:col-span-3',
    size: 'md',
  },
  {
    num: '06',
    icon: Palette,
    title: 'Product Design',
    desc: 'Research-driven UX, pixel-perfect UI systems, and brand identity that ships.',
    tools: ['Figma', 'Framer', 'Webflow', 'Principle'],
    accent: '#FF8C42',
    span: 'md:col-span-6',
    size: 'wide',
  },
];

function ServiceCard({ s }) {
  const isLg = s.size === 'lg';
  const isWide = s.size === 'wide';
  const Icon = s.icon;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true, margin: '-40px' }}
      whileHover="hover"
      className={`group relative ${s.span} rounded-2xl overflow-hidden cursor-pointer min-h-[220px]`}
      style={{ background: 'rgba(10,10,24,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={`service-card-${s.num}`}
    >
      {/* Hover accent glow */}
      <motion.div
        variants={{
          hover: { opacity: 0.18 },
        }}
        initial={{ opacity: 0 }}
        className="absolute -top-1/2 -left-1/4 w-[160%] h-[160%] pointer-events-none"
        style={{ background: `radial-gradient(circle at 30% 30%, ${s.accent}, transparent 55%)` }}
      />
      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(rgba(255,255,255,0.35) 0.8px, transparent 0.8px)`,
          backgroundSize: '18px 18px',
          maskImage: 'linear-gradient(180deg, black 0%, transparent 90%)',
          WebkitMaskImage: 'linear-gradient(180deg, black 0%, transparent 90%)',
        }}
      />
      {/* Corner number */}
      <span
        className="absolute top-5 right-6 font-mono font-bold select-none pointer-events-none tracking-tighter leading-none"
        style={{
          color: 'rgba(255,255,255,0.05)',
          fontSize: isLg ? '9rem' : isWide ? '7rem' : '5rem',
        }}
      >
        {s.num}
      </span>

      {/* Animated accent line */}
      <motion.div
        variants={{ hover: { width: '100%' } }}
        initial={{ width: '0%' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="absolute top-0 left-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${s.accent}, transparent)` }}
      />

      {isWide ? (
        // Wide horizontal card (Product Design)
        <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row gap-8 items-start md:items-center h-full">
          <motion.div
            variants={{ hover: { rotate: -6, scale: 1.08 } }}
            className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: `${s.accent}18`, border: `1px solid ${s.accent}30` }}
          >
            <Icon className="w-7 h-7" style={{ color: s.accent }} />
          </motion.div>
          <div className="flex-1">
            <h3 className="text-2xl md:text-3xl font-bold font-space text-white mb-2">{s.title}</h3>
            <p className="text-[#8A8AA3] text-base leading-relaxed max-w-2xl">{s.desc}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {s.tools.map(t => (
              <span key={t} className="text-[11px] font-mono text-white/70 bg-white/[0.03] border border-white/10 px-3 py-1.5 rounded-full">
                {t}
              </span>
            ))}
          </div>
          <motion.div
            variants={{ hover: { x: 4, y: -4 } }}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: `${s.accent}15`, border: `1px solid ${s.accent}40` }}
          >
            <ArrowUpRight className="w-4 h-4" style={{ color: s.accent }} />
          </motion.div>
        </div>
      ) : (
        <div className="relative z-10 p-7 md:p-8 flex flex-col h-full">
          <motion.div
            variants={{ hover: { rotate: -6, scale: 1.08 } }}
            className={`${isLg ? 'w-16 h-16' : 'w-12 h-12'} rounded-2xl flex items-center justify-center mb-5`}
            style={{ background: `${s.accent}18`, border: `1px solid ${s.accent}30` }}
          >
            <Icon className={`${isLg ? 'w-7 h-7' : 'w-5 h-5'}`} style={{ color: s.accent }} />
          </motion.div>

          <h3
            className={`font-bold font-space text-white mb-3 tracking-tight ${
              isLg ? 'text-3xl md:text-4xl' : 'text-xl md:text-2xl'
            }`}
          >
            {s.title}
          </h3>
          <p className={`text-[#8A8AA3] leading-relaxed ${isLg ? 'text-base max-w-md' : 'text-sm'} mb-5`}>
            {s.desc}
          </p>

          {isLg && s.services && (
            <ul className="space-y-2 mb-6">
              {s.services.map(sv => (
                <li key={sv} className="flex items-center gap-2 text-sm text-white/75">
                  <div className="w-1 h-1 rounded-full" style={{ background: s.accent }} />
                  {sv}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-auto flex items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5">
              {s.tools.slice(0, isLg ? 4 : 3).map(t => (
                <span
                  key={t}
                  className="text-[10px] font-mono text-white/60 bg-white/[0.03] border border-white/10 px-2 py-1 rounded-full"
                >
                  {t}
                </span>
              ))}
            </div>
            <motion.div
              variants={{ hover: { x: 4, y: -4 } }}
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: `${s.accent}15`, border: `1px solid ${s.accent}40` }}
            >
              <ArrowUpRight className="w-4 h-4" style={{ color: s.accent }} />
            </motion.div>
          </div>
        </div>
      )}
    </motion.article>
  );
}

export default function ServicesGrid() {
  return (
    <section id="services" className="relative py-24 md:py-32" style={{ background: 'rgba(5,5,16,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8">
        <SectionLabel>Our Services</SectionLabel>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-[clamp(2rem,4vw,3.5rem)] font-bold font-space text-[#F0F0F5] tracking-[-0.02em] mb-4"
        >
          What We Build
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-[#8A8AA3] text-lg mb-12 md:mb-16 max-w-2xl"
        >
          End-to-end digital solutions, from strategy to deployment and beyond.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5 auto-rows-[minmax(220px,_auto)]">
          {services.map(s => (
            <ServiceCard key={s.num} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
